/**
 * PCWEvaluationDialog - PCW Performance Evaluation dialog
 * Extracted from HRPayrollPage to reduce file size
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText } from "lucide-react";

function getGradeColor(grade) {
  switch (grade) {
    case 1: return "bg-emerald-100 text-emerald-800";
    case 2: return "bg-green-100 text-green-800";
    case 3: return "bg-yellow-100 text-yellow-800";
    case 4: return "bg-orange-100 text-orange-800";
    case 5: return "bg-red-100 text-red-800";
    default: return "bg-slate-100 text-slate-600";
  }
}

export function PCWEvaluationDialog({
  open,
  onOpenChange,
  employees,
  pcwDuties,
  newPcwEvaluation,
  setNewPcwEvaluation,
  saving,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            PCW Performance Evaluation
          </DialogTitle>
          <DialogDescription>
            Evaluate Personal Care Worker based on their duties (Blessed Health Care Form)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Employee & Evaluator Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select
                value={newPcwEvaluation.employee_id}
                onValueChange={(v) => {
                  const emp = employees.find(e => e.id === v);
                  setNewPcwEvaluation(prev => ({ 
                    ...prev, 
                    employee_id: v,
                    employee_name: emp?.name || ""
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select PCW employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.role === "pcw").map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Evaluation Date *</Label>
              <Input
                type="date"
                value={newPcwEvaluation.evaluation_date}
                onChange={(e) => setNewPcwEvaluation(prev => ({ ...prev, evaluation_date: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Person Completing Evaluation *</Label>
              <Input
                value={newPcwEvaluation.evaluator_name}
                onChange={(e) => setNewPcwEvaluation(prev => ({ ...prev, evaluator_name: e.target.value }))}
                placeholder="RN or Supervisor name"
              />
            </div>
            <div className="space-y-2">
              <Label>Supervisor Name</Label>
              <Input
                value={newPcwEvaluation.supervisor_name}
                onChange={(e) => setNewPcwEvaluation(prev => ({ ...prev, supervisor_name: e.target.value }))}
                placeholder="Supervisor name"
              />
            </div>
            <div className="space-y-2">
              <Label>Evaluation Period Start</Label>
              <Input
                type="date"
                value={newPcwEvaluation.period_start}
                onChange={(e) => setNewPcwEvaluation(prev => ({ ...prev, period_start: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Evaluation Period End</Label>
              <Input
                type="date"
                value={newPcwEvaluation.period_end}
                onChange={(e) => setNewPcwEvaluation(prev => ({ ...prev, period_end: e.target.value }))}
              />
            </div>
          </div>

          {/* Grading Scale Reference */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <h4 className="text-sm font-medium text-slate-700 mb-2">Grading Scale</h4>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">1 = Excellent</span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">2 = Very Good</span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">3 = Average</span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">4 = Below Average</span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">5 = Poor</span>
            </div>
          </div>

          {/* Duty Ratings */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-800">PCW Duties Evaluation</h4>
            
            {pcwDuties.map((duty) => (
              <div key={duty.id} className="p-4 border border-slate-200 rounded-lg">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">
                      {duty.number}. {duty.description}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((grade) => (
                      <button
                        key={grade}
                        type="button"
                        onClick={() => setNewPcwEvaluation(prev => ({ ...prev, [duty.id]: grade }))}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                          newPcwEvaluation[duty.id] === grade
                            ? getGradeColor(grade) + " ring-2 ring-offset-2 ring-slate-400"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {grade}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Goals Section (for RN) */}
          <div className="p-4 bg-blue-50 rounded-lg space-y-4">
            <h4 className="font-medium text-blue-800">Goals (Set by RN/Supervisor)</h4>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-blue-700">Goal 1</Label>
                <Textarea
                  value={newPcwEvaluation.goal_1}
                  onChange={(e) => setNewPcwEvaluation(prev => ({ ...prev, goal_1: e.target.value }))}
                  placeholder="Enter first goal for improvement..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-blue-700">Goal 2</Label>
                <Textarea
                  value={newPcwEvaluation.goal_2}
                  onChange={(e) => setNewPcwEvaluation(prev => ({ ...prev, goal_2: e.target.value }))}
                  placeholder="Enter second goal..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-blue-700">Goal 3</Label>
                <Textarea
                  value={newPcwEvaluation.goal_3}
                  onChange={(e) => setNewPcwEvaluation(prev => ({ ...prev, goal_3: e.target.value }))}
                  placeholder="Enter third goal..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Comments */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Additional Comments (Evaluator)</Label>
              <Textarea
                value={newPcwEvaluation.additional_comments}
                onChange={(e) => setNewPcwEvaluation(prev => ({ ...prev, additional_comments: e.target.value }))}
                placeholder="Additional observations or comments..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Employee Comments</Label>
              <Textarea
                value={newPcwEvaluation.employee_comments}
                onChange={(e) => setNewPcwEvaluation(prev => ({ ...prev, employee_comments: e.target.value }))}
                placeholder="Space for employee to add their comments..."
                rows={3}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save Evaluation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
